import React, { useEffect, useRef, useState } from "react";
import { Search, UserCheck, ExternalLink } from "lucide-react";
import Header from "../Common/AdminHeader";
import axios from "axios";
import { Config } from "../../../API/config";
import toast from "react-hot-toast";
import TraineeViewCard from "../../../Shared components/TraineeViewCard";

interface User {
    _id: string;
    name: string;
    email: string;
    phone: string;
    block: boolean;
    city: string;
    government: string;
    gender: "male" | "female";
    createdAt: string;
}

const ManageAllUsers: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(false);
    const [buttonLoading, setButtonLoading] = useState<string | null>(null);
    const dialogRef = useRef<HTMLDivElement>(null);

    const fetchTrainees = async () => {
        setLoading(true);
        try {
            let { data } = await axios.get(Config.getAllTrainees, { withCredentials: true });
            
            if (Array.isArray(data)) {
                setUsers(data);
                setFilteredUsers(data);
            } else {
                setUsers([]);
                setFilteredUsers([]);
            }
        } catch (err) {
            console.error("Error fetching trainees", err);
            setUsers([]);
            setFilteredUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const downloadTraineesExcelSheet = async () => {
        try {
            const response = await axios.get('https://otlobcoach.com/api/v1/user/getAllTraineesSheet', {
                withCredentials: true,
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Trainees.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success("تم تحميل ملف Excel للمتدربين بنجاح");
        } catch (err) {
            console.error("Error downloading trainees Excel sheet:", err);
            toast.error("حدث خطأ أثناء تحميل ملف Excel للمتدربين");
        }
    };
    const blockTrainer = async (trainerId: string) => {
        setButtonLoading(trainerId);
        try {
            const trainer = filteredUsers.find(t => t._id === trainerId);
            const currentBlockStatus = trainer?.block;

            const { data } = await axios.patch(
                `${Config.BlockTrainer}/${trainerId}`,
                {},
                { withCredentials: true }
            );
            
            if (data) {
                setFilteredUsers(prev =>
                    prev.map(t =>
                        t._id === trainerId ? { ...t, block: !currentBlockStatus } : t
                    )
                );
                toast.success(currentBlockStatus ? "تم فك حظر المدرب" : "تم حظر المدرب");
            }
        } catch (err) {
            toast.error("فشل في تعديل حالة المدرب");
            console.error("Error updating trainer status:", err);
        } finally {
            setButtonLoading(null);
        }
    };
    const handleDownloadTraineeEnrollments = async (traineeId: string) => {
        try {
            const response = await axios.get(
                `${Config.downloadEnrollments}/?traineeId=${encodeURIComponent(traineeId)}`,
                {
                    withCredentials: true,
                    responseType: 'blob',
                }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Trainee_Enrollments_${traineeId}_${new Date().toLocaleDateString("ar-EG")}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success("تم تحميل ملف Excel للاشتراكات بنجاح");
        } catch (err) {
            console.error("Error downloading trainee enrollments Excel file:", err);
            toast.error("فشل في تحميل ملف Excel للاشتراكات");
        }
    };

    useEffect(() => {
        fetchTrainees();
    }, []);

    const handleSearch = (query: string) => {
        setSearchQuery(query);
        if (query.trim() === "") {
            setFilteredUsers(users);
        } else {
            const filtered = users.filter(user =>
                user.name.toLowerCase().includes(query.toLowerCase()) ||
                user.email.toLowerCase().includes(query.toLowerCase()) ||
                user.phone.includes(query)
            );
            setFilteredUsers(filtered);
        }
    };

    return (
        <div className='flex-1 overflow-auto relative z-10 bg-white'>
            <Header title={"ادارة المتدربين"} />

            <main className='max-w-7xl mx-auto py-6 px-4 lg:px-8'>
                <div className="bg-white border rounded-lg p-4 w-full max-w-3xl mx-auto mt-6">
                    <div className="flex justify-between items-center border-b pb-3">
                        <div className="flex items-center space-x-reverse space-x-3">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="ابحث عن مستخدم..."
                                    value={searchQuery}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    className="pr-10 pl-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                                />
                                <Search
                                    size={20}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                />
                            </div>
                        </div>
                        <div className="flex items-center space-x-reverse space-x-2">
                            <UserCheck size={20} className="text-gray-700" />
                            <h2 className="text-lg font-semibold text-gray-900">جميع المستخدمين</h2>
                            <button
                                onClick={downloadTraineesExcelSheet}
                                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                            >
                                تحميل ملف Excel
                            </button>
                        </div>
                    </div>

                    <div className="mt-4 space-y-4">
                        {loading ? (
                            <p className="text-center text-gray-500">جاري التحميل...</p>
                        ) : filteredUsers.length > 0 ? (
                            filteredUsers.map((user, index) => (
                                <div
                                    key={index}
                                    className="flex flex-row-reverse items-center justify-between p-2 bg-gray-100 rounded-lg"
                                >
                                    <div className="flex flex-row-reverse items-center space-x-reverse space-x-3">
                                        <div className="text-right items-center space-x-reverse">
                                            <p className="text-gray-900 font-medium">{user.name}</p>
                                            <p className="text-gray-600 text-sm">{user.phone || 'غير متوفر'} : الهاتف </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-reverse space-x-2">
                                        <ExternalLink
                                            size={18}
                                            className="text-gray-500 cursor-pointer mr-4"
                                            onClick={() => setSelectedUser(user)}
                                        />
                                        <button
                                            onClick={() => blockTrainer(user._id)}
                                            disabled={buttonLoading === user._id}
                                            className={`px-3 py-1 rounded-lg transition text-white ${user.block
                                                ? 'bg-green-500 hover:bg-green-600'
                                                : 'bg-red-500 hover:bg-red-600'
                                                } ${buttonLoading === user._id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            {buttonLoading === user._id ? '...جارٍ' : (user.block ? 'فك الحظر' : 'حظر')}
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-center text-gray-500">لا يوجد مستخدمين مطابقين</p>
                        )}
                    </div>
                </div>
            </main >

            {selectedUser && (
                <TraineeViewCard
                    selectedUser={selectedUser}
                    setSelectedUser={setSelectedUser}
                    users={users}
                    setUsers={setUsers}
                    setFilteredUsers={setFilteredUsers}
                    handleDownloadTraineeEnrollments={handleDownloadTraineeEnrollments}
                />
            )}
        </div >
    );
};

export default ManageAllUsers;